import React, { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Rating,
  Paper,
  ImageList,
  ImageListItem
} from '@mui/material'
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Cloud as WeatherIcon,
  WineBar as PairingIcon,
  Thermostat as TempIcon,
  People as PeopleIcon,
  Chair as SettingIcon,
  CalendarToday as CalendarIcon,
  Timer as TimerIcon
} from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'
import { useGetSessionQuery, useDeleteSessionMutation } from '@store/api/sessionApi'
import { SmokingSession } from '@store/api/sessionApi'
import { format } from 'date-fns'
import { RATING_FACTOR_META, getScoreLabel } from '../constants/ratingFactors'
import SessionForm from '@components/sessions/SessionForm'

const SessionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [editingSession, setEditingSession] = useState<SmokingSession | null>(null)
  const [showForm, setShowForm] = useState(false)

  const { data: session, isLoading, error } = useGetSessionQuery(id!, { skip: !id })
  const [deleteSession] = useDeleteSessionMutation()

  const formatDuration = (minutes?: number) => {
    if (!minutes) return 'N/A'
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`
  }

  const getQualityColor = (quality?: string) => {
    switch (quality) {
      case 'excellent': return 'success'
      case 'good': return 'info'
      case 'fair': return 'warning'
      case 'poor': return 'error'
      default: return 'default'
    }
  }

  const getAshColor = (color?: string) => {
    switch (color) {
      case 'white': return '#f5f5f5'
      case 'gray': return '#9e9e9e'
      case 'black': return '#424242'
      case 'mixed': return '#ff9800'
      default: return '#e0e0e0'
    }
  }

  const handleEdit = () => {
    if (session) {
      setEditingSession(session)
      setShowForm(true)
    }
  }

  const handleDelete = async () => {
    if (!session) return
    if (window.confirm('Are you sure you want to delete this session?')) {
      try {
        await deleteSession(session.id).unwrap()
        navigate('/app/journal')
      } catch (error) {
        console.error('Failed to delete session:', error)
      }
    }
  }

  const handleCloseForm = () => {
    setShowForm(false)
    setEditingSession(null)
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !session) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/app/journal')} sx={{ mb: 2 }}>
          Back to Journal
        </Button>
        <Alert severity="error">
          Session not found or could not be loaded.
        </Alert>
      </Box>
    )
  }

  return (
    <Box>
      {/* Navigation and actions */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/app/journal')}>
          Back to Journal
        </Button>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEdit}>
            Edit
          </Button>
          <IconButton color="error" onClick={handleDelete} aria-label="Delete session">
            <DeleteIcon />
          </IconButton>
        </Stack>
      </Box>

      {/* Header */}
      <Box mb={3}>
        {session.isDraft && (
          <Chip label="Draft" color="warning" size="small" sx={{ mb: 1 }} />
        )}
        <Typography variant="h4" component="h1" gutterBottom>
          {session.cigar.cigarLine.brand.name} {session.cigar.cigarLine.name}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {session.cigar.vitola.name}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Main info */}
        <Grid item xs={12} md={8}>
          {/* Images */}
          {session.imageUrls && session.imageUrls.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Photos
                </Typography>
                <ImageList cols={session.imageUrls.length === 1 ? 1 : 2} gap={8}>
                  {session.imageUrls.map((url, index) => (
                    <ImageListItem key={index}>
                      <img
                        src={url}
                        alt={`Session photo ${index + 1}`}
                        loading="lazy"
                        style={{ borderRadius: 4, maxHeight: 300, objectFit: 'cover', width: '100%' }}
                      />
                    </ImageListItem>
                  ))}
                </ImageList>
              </CardContent>
            </Card>
          )}

          {/* Notes section */}
          {(session.preSmokingNotes || session.tastingNotes) && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Notes
                </Typography>

                {session.preSmokingNotes && (
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" fontWeight="bold" gutterBottom>
                      Pre-Smoking Observations
                    </Typography>
                    <Typography variant="body1">
                      {session.preSmokingNotes}
                    </Typography>
                  </Box>
                )}

                {session.preSmokingNotes && session.tastingNotes && <Divider sx={{ my: 2 }} />}

                {session.tastingNotes && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight="bold" gutterBottom>
                      Tasting Notes
                    </Typography>
                    <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                      &ldquo;{session.tastingNotes}&rdquo;
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Quality indicators */}
          {(session.drawQuality || session.burnQuality || session.ashColor) && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Quality Assessment
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  {session.drawQuality && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Draw</Typography>
                      <Chip
                        label={session.drawQuality}
                        color={getQualityColor(session.drawQuality) as any}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </Box>
                  )}
                  {session.burnQuality && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Burn</Typography>
                      <Chip
                        label={session.burnQuality}
                        color={getQualityColor(session.burnQuality) as any}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </Box>
                  )}
                  {session.ashColor && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Ash Color</Typography>
                      <Chip
                        label={session.ashColor}
                        size="small"
                        sx={{
                          textTransform: 'capitalize',
                          bgcolor: getAshColor(session.ashColor),
                          color: session.ashColor === 'black' ? 'white' : 'black'
                        }}
                      />
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Sidebar details */}
        <Grid item xs={12} md={4}>
          {/* Multi-Factor Rating */}
          {session.sessionRating && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Multi-Factor Rating
                </Typography>
                <Typography variant="h4" color="primary" fontWeight={700} gutterBottom>
                  {session.sessionRating.overall}/10 overall
                </Typography>
                <Stack spacing={1.5}>
                  {RATING_FACTOR_META.map((factor) => {
                    type NumericRatingKey = 'flavor' | 'construction' | 'burn' | 'value' | 'overall'
                    type NoteRatingKey = 'flavorNotes' | 'constructionNotes' | 'burnNotes' | 'valueNotes' | 'overallNotes'
                    const rating = session.sessionRating!
                    const score = rating[factor.key as NumericRatingKey]
                    const notes = rating[factor.notesKey as NoteRatingKey] ?? null
                    return (
                      <Box key={factor.key}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" fontWeight={500}>{factor.label}</Typography>
                          <Chip
                            size="small"
                            label={`${score}/10 · ${getScoreLabel(score, factor.lowLabel, factor.highLabel)}`}
                          />
                        </Box>
                        {notes && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            {notes}
                          </Typography>
                        )}
                      </Box>
                    )
                  })}
                </Stack>
                {session.sessionRating.overallNotes && (
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    {session.sessionRating.overallNotes}
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}

          {/* Legacy star rating fallback */}
          {!session.sessionRating && session.rating != null && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Rating
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Rating value={session.rating} precision={0.5} readOnly />
                  <Typography variant="h6">{session.rating}/5</Typography>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Session info */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Session Details
              </Typography>
              <Stack spacing={1.5}>
                <Box display="flex" alignItems="flex-start" gap={1.5}>
                  <CalendarIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">Date</Typography>
                    <Typography variant="body2">
                      {format(new Date(session.smokedDate), 'MMMM dd, yyyy')}
                    </Typography>
                  </Box>
                </Box>

                {session.durationMinutes != null && (
                  <Box display="flex" alignItems="flex-start" gap={1.5}>
                    <TimerIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Duration</Typography>
                      <Typography variant="body2">{formatDuration(session.durationMinutes)}</Typography>
                    </Box>
                  </Box>
                )}

                {session.startTime && (
                  <Box display="flex" alignItems="flex-start" gap={1.5}>
                    <TimeIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Started</Typography>
                      <Typography variant="body2">
                        {format(new Date(session.startTime), 'h:mm a')}
                        {session.endTime && ` – ${format(new Date(session.endTime), 'h:mm a')}`}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {session.location && (
                  <Box display="flex" alignItems="flex-start" gap={1.5}>
                    <LocationIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Location</Typography>
                      <Typography variant="body2">{session.location}</Typography>
                    </Box>
                  </Box>
                )}

                {session.setting && (
                  <Box display="flex" alignItems="flex-start" gap={1.5}>
                    <SettingIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Setting</Typography>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{session.setting}</Typography>
                    </Box>
                  </Box>
                )}

                {session.weather && (
                  <Box display="flex" alignItems="flex-start" gap={1.5}>
                    <WeatherIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Weather</Typography>
                      <Typography variant="body2">{session.weather}</Typography>
                    </Box>
                  </Box>
                )}

                {(session.temperature != null || session.humidity != null) && (
                  <Box display="flex" alignItems="flex-start" gap={1.5}>
                    <TempIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Environment</Typography>
                      <Typography variant="body2">
                        {session.temperature != null && `${session.temperature}°F`}
                        {session.temperature != null && session.humidity != null && ' • '}
                        {session.humidity != null && `${session.humidity}% humidity`}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {session.pairing && (
                  <Box display="flex" alignItems="flex-start" gap={1.5}>
                    <PairingIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Pairing</Typography>
                      <Typography variant="body2">{session.pairing}</Typography>
                    </Box>
                  </Box>
                )}

                {session.companions && session.companions.length > 0 && (
                  <Box display="flex" alignItems="flex-start" gap={1.5}>
                    <PeopleIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Companions</Typography>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" mt={0.5}>
                        {session.companions.map((name, i) => (
                          <Chip key={i} label={name} size="small" variant="outlined" />
                        ))}
                      </Stack>
                    </Box>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Metadata */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Recorded: {format(new Date(session.createdAt), 'MMM dd, yyyy h:mm a')}
            </Typography>
            {session.updatedAt !== session.createdAt && (
              <Typography variant="caption" color="text.secondary" display="block">
                Updated: {format(new Date(session.updatedAt), 'MMM dd, yyyy h:mm a')}
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Edit Form */}
      <SessionForm
        open={showForm}
        onClose={handleCloseForm}
        session={editingSession}
      />
    </Box>
  )
}

export default SessionDetail
